'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { notFound, useParams, usePathname, useRouter } from 'next/navigation';
import { overlay } from 'overlay-kit';
import { useEffect, useMemo } from 'react';

import BottomSheet from '@/components/common/bottomSheets';
import AlertDialog from '@/components/common/dialog/alertDialog';
import MemulDetailBottom from '@/components/memul/detail/bottom';
import OftenMemulBottomSheet from '@/components/memul/detail/oftenMemulBotoomSheet';
import MemulDetailTop from '@/components/memul/detail/top';
import { APT_TYPE, OFFICETEL_TYPE } from '@/const/buildingType';
import memulQueries from '@/service/memul/memulQueries';
import useMobileStore from '@/store/mobileStore';
import { usePathStore } from '@/store/pathStore';
import { handleSendGAEvent } from '@/utils/google';
import { appToClose, appToRecent } from '@/utils/js/webView';

const SummaryInfo = dynamic(() => import('@/components/memul/detail/sub/summaryInfo'), {
  ssr: false,
});
const ActualTransactionPrice = dynamic(
  () => import('@/components/memul/detail/sub/actualTransactionPrice'),
  {
    ssr: false,
  },
);
const MemulInfo = dynamic(() => import('@/components/memul/detail/sub/memulInfo'), {
  ssr: false,
});
const SurroundingEnv = dynamic(() => import('@/components/memul/detail/sub/surroundingEnv'), {
  ssr: false,
});
const RealtorOffice = dynamic(() => import('@/components/memul/detail/sub/realtorOffice'), {
  ssr: false,
});
const LoanInterest = dynamic(() => import('@/components/memul/detail/sub/loanInterest'), {
  ssr: false,
});
const SimilarMemuls = dynamic(() => import('@/components/memul/detail/sub/similarMemuls'), {
  ssr: false,
});

export default function MemulDetail() {
  const router = useRouter();
  const { articleCd } = useParams();
  const { isWebView } = useMobileStore();
  const { noPreviousPath, resetPath } = usePathStore();
  const pathName = usePathname();

  const { data } = useSuspenseQuery({
    ...memulQueries.memulDetail(articleCd as string),
    select: (data) => data.data,
  });

  const { data: checkData } = useSuspenseQuery({
    ...memulQueries.memulCheck(articleCd as string),
    select: (data) => data.data,
  });

  const oftenMemulBottomSheet = () => {
    // 바텀시트 - 자주 본 매물
    if (isWebView) {
      overlay.open(
        ({ isOpen, close }) => {
          return (
            <BottomSheet isOpen={isOpen} close={close} title="자주 본 매물">
              <OftenMemulBottomSheet close={close} data={data} />
            </BottomSheet>
          );
        },
        { overlayId: 'oftenMemul' },
      );
    }
  };

  // 툴팁 애니메이션 - 인기 매물
  const fadeOutTooltip = () => {
    setTimeout(() => {
      const tooltip = document.querySelector('.tooltip.popular_memul');

      if (tooltip) {
        tooltip.classList.add('fade_out');
      }
    }, 3000);
  };

  useEffect(() => {
    fadeOutTooltip();
    if (isWebView) {
      handleSendGAEvent('page_view', {
        event_category: 'memul',
        page_title: 'memul_memul_detail',
        page_referrer: window.location.href,
        memul_type: data?.businessDivNm,
        memul_id: data?.articleCd,
      });
    } else {
      handleSendGAEvent('page_view', {
        event_category: 'share',
        page_title: 'share_memul',
        page_referrer: window.location.href,
        memul_type: data?.businessDivNm,
        memul_id: data?.articleCd,
      });
    }
  }, []);

  const isCommonBuilding = useMemo(
    () => Object.keys({ ...APT_TYPE, ...OFFICETEL_TYPE }).includes(data?.buildingTypeCd),
    [data?.buildingTypeCd],
  );

  useEffect(() => {
    // if (data?.code === 9902) notFound();
    if (checkData?.displayYn === 'N') {
      overlay.unmountAll();
      overlay.open(({ isOpen, close }) => {
        const handleSuccess = () => {
          if (isWebView && pathName === noPreviousPath) {
            resetPath();
            appToClose();
          } else {
            router.back();
          }
        };

        return (
          <AlertDialog
            isOpen={isOpen}
            close={close}
            title="노출 종료된 매물입니다"
            // desc="매물리스트로 이동하여 다른 매물도 확인해보세요"
            successCb={handleSuccess}
            dimCb={handleSuccess}
          />
        );
      });
    }
  }, [checkData]);

  useEffect(() => {
    if (!data.articleCd) return;
    const viewPageStr = sessionStorage.getItem('viewPageArr');

    // 페이지 뷰 카운트 증가
    const updatePageViewCount = () => {
      let viewPageData = viewPageStr ? JSON.parse(viewPageStr) : {};
      const currentTime = Date.now();

      // 새 항목 추가 또는 업데이트
      if (!viewPageData[data.articleCd]) {
        viewPageData[data.articleCd] = {
          count: 0,
          timestamp: currentTime,
        };
      }
      viewPageData[data.articleCd].count += 1;

      // 10개 초과시 가장 오래된 항목 삭제
      const keys = Object.keys(viewPageData);
      if (keys.length > 50) {
        const oldestKey = keys.reduce((oldest, key) => {
          return viewPageData[key].timestamp < viewPageData[oldest].timestamp ? key : oldest;
        }, keys[0]);
        delete viewPageData[oldestKey];
      }

      sessionStorage.setItem('viewPageArr', JSON.stringify(viewPageData));
    };

    // 이미 본 기록이 있더라도 카운트는 증가
    updatePageViewCount();

    if (viewPageStr && JSON.parse(viewPageStr)[data.articleCd]?.count >= 3) {
      oftenMemulBottomSheet();
    }

    // 최근조회 앱으로 전송
    if (articleCd) appToRecent({ key: 'offering', value: articleCd as string });
  }, [data.articleCd]);

  if (checkData?.displayYn !== 'N' && data?.code === 9902) notFound();

  return (
    <div className="property_detail">
      <MemulDetailTop />
      <div id="container">
        <SummaryInfo /> {/* 요약정보 */}
        {isCommonBuilding && <ActualTransactionPrice />} {/* 동일 면적 실거래 정보 */}
        <MemulInfo /> {/* 매물 정보 */}
        <SurroundingEnv /> {/* 주변 환경 */}
        <RealtorOffice /> {/* 중개사무소 */}
        {isCommonBuilding && data.businessDiv !== 'T' && <LoanInterest />}
        {/* 대출 이자 계산기 */}
        <SimilarMemuls /> {/* 유사 매물 */}
      </div>
      <MemulDetailBottom />
    </div>
  );
}
